import { Box, Tooltip } from '@mui/material';
import { Column } from 'react-data-grid';
import RowTypography from 'components/Table/RowTypography';
import BulkUploadDialog from 'components/Common/BulkUploadDialog';
import * as XLSX from 'xlsx';
import { ReactComponent as WarningIcon } from '../../assets/warning-dp-job.svg';
import { METADATA_APPLICABILITY, PLATFORM_METADATA_APPLICABILITY, METADATA_REQUIRED_HEADERS, sampleRows } from './constants/metadataConstants';
import { uploadJobMetadata } from 'service/service';

interface MetadataRow {
  [key: string]: any;
  id: number;
  env: string;
  platform_name: string;
  scheduler_name: string;
  workspace_name: string;
  app_name: string;
  schedtab: string;
  job_id: string;
  job_name: string;
  sector: string;
  program: string;
  domain: string;
  gdo: string;
  source_category: string;
  source_subcategory: string;
  orchestrator: string;
  business_unit: string;
  department: string;
  capability: string;
  technology: string;
  dataasset_name: string;
  bronze_table: string;
  silver_table: string;
  gold_table: string;
  sustain_owner: string;
  sustain_prod_date: string;
  layer: string;
  market: string;
  market_group: string;
  to_be_deleted: string;
  selected?: boolean;
  isValid?: boolean;
  errors?: string[];
  business_stream?: string[];
  business_application?: string[];
}

interface MetadataUpdatesProps {
  open: boolean;
  onCloseAction: (showQueryBuilder?: boolean) => void;
}

const normalizeHeaderKey = (header: string) =>
  header.trim().toLowerCase().replace(/[\s_]+/g, '_');

const formatFieldLabel = (key: string) =>
  key
    .split('_')
    .map((segment) => (segment ? segment.charAt(0).toUpperCase() + segment.slice(1) : segment))
    .join(' ');

const normalizeMetadataRow = (row: Record<string, any>): MetadataRow => {
  const normalizedRow: Record<string, any> = {};

  METADATA_REQUIRED_HEADERS.forEach((key) => {
    const normalizedKey = normalizeHeaderKey(key);
    const matchingKey = Object.keys(row).find((candidate) => normalizeHeaderKey(candidate) === normalizedKey);

    normalizedRow[key] = matchingKey == null ? '' : row[matchingKey];
  });

  return normalizedRow as MetadataRow;
};

const formatNormalizedDate = (year: number, month: number, day: number) => {
  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
};

const normalizeDateValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (value instanceof Date) {
    return formatNormalizedDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const parsedDate = new Date(excelEpoch.getTime() + value * 86400000);
    return formatNormalizedDate(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth() + 1, parsedDate.getUTCDate());
  }

  let normalized = '';
  if (typeof value === 'string') {
    normalized = value.trim();
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    normalized = String(value).trim();
  }

  if (!normalized) {
    return '';
  }

  const isoMatch = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/.exec(normalized);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return formatNormalizedDate(Number(year), Number(month), Number(day));
  }

  const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:[T\s].*)?$/.exec(normalized);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    const parsedYear = Number(year) < 100 ? Number(year) + 2000 : Number(year);
    return formatNormalizedDate(parsedYear, Number(month), Number(day));
  }

  if (/^\d+$/.test(normalized)) {
    const excelSerial = Number(normalized);

    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const parsedDate = new Date(excelEpoch.getTime() + excelSerial * 86400000);

    return formatNormalizedDate(
      parsedDate.getUTCFullYear(),
      parsedDate.getUTCMonth() + 1,
      parsedDate.getUTCDate(),
    );
  }

  const parsedDate = new Date(normalized);
  if (!Number.isNaN(parsedDate.getTime())) {
    return formatNormalizedDate(parsedDate.getFullYear(), parsedDate.getMonth() + 1, parsedDate.getDate());
  }

  return normalized;
};

const normalizeCellValue = (value: unknown, field?: string) => {
  const normalized =
    field?.toLowerCase().includes("date")
      ? normalizeDateValue(value)
      : String(value ?? "").trim();

  if (!normalized) {
    return '';
  }

  const upper = normalized.toUpperCase();

  if (
    upper === 'OPTIONAL' ||
    upper === 'NOT APPLICABLE' ||
    upper === 'APPLICABLE' ||
    upper === '--'
  ) {
    return '';
  }

  return normalized;
};
const isPlaceholderValue = (value: string) => {
  return ['APPLICABLE', 'OPTIONAL', 'NOT APPLICABLE', '--', ''].includes(
    value.toUpperCase()
  );
};

const isValidDate = (value: unknown) => {
  if (value == null || value === '') {
    return true;
  }

  const dateValue = normalizeDateValue(value);
  if (!dateValue) {
    return true;
  }

  const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}$/;

  if (!regex.test(dateValue)) {
    return false;
  }

  const [monthInput, dayInput, yearInput] = dateValue.split('/').map(Number);
  const month = monthInput;
  const day = dayInput;
  let year = yearInput;
  if (year < 100) {
    year += 2000;
  }
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const getRowErrors = (row: MetadataRow): string[] => {
  const errors: string[] = [];

  // Platform name is mandatory for every row.
  const platformKey = String(row.platform_name ?? '').trim().toLowerCase();
  if (!platformKey) {
    errors.push('Platform name is required.');
    return errors;
  }

  const applicabilityRules = PLATFORM_METADATA_APPLICABILITY[platformKey] || METADATA_APPLICABILITY;

  const hasAssetInformation = ['dataasset_name', 'bronze_table', 'silver_table'].some((field) => {
    const value = normalizeCellValue(row[field], field);
    return value !== '';
  });

  const isAdfPlatform = platformKey === 'adf';

  // For each metadata key, enforce applicability rules.
  Object.keys(applicabilityRules).forEach((key) => {
    const value = normalizeCellValue(row[key], key);

    if (isAdfPlatform && key === 'job_name' && isPlaceholderValue(value)) {
      if (hasAssetInformation) {
        return;
      }
      errors.push(
        'Job name is required for ADF unless dataasset_name, bronze_table, or silver_table is provided.'
      );
      return;
    }

    if (applicabilityRules[key] === 'APPLICABLE' && isPlaceholderValue(value)) {
      errors.push(`${key.replace(/_/g, ' ')} is required.`);
    }
    // Validate SUSTAIN_PROD_DATE format
    if (key === 'sustain_prod_date' && value && !isValidDate(value)) {
      errors.push('SUSTAIN_PROD_DATE must be in MM/DD/YYYY format.');
    }
  });

  return errors;
};

const TextFormatter = ({ row, field }: { row: MetadataRow; field: string }) => {
  const raw = row[field];

  // Get platform-specific applicability rules
  const platformKey = String(row.platform_name ?? '').trim().toLowerCase();
  const applicabilityRules = PLATFORM_METADATA_APPLICABILITY[platformKey] || METADATA_APPLICABILITY;
  const value = normalizeCellValue(raw, field);
  const rule = applicabilityRules[String(field)];

  if (rule === 'OPTIONAL' || rule === 'NOT APPLICABLE') {
    return <RowTypography>{value || '--'}</RowTypography>;
  }

  if (!value || value.toUpperCase() === 'APPLICABLE') {
    return <RowTypography>{'--'}</RowTypography>;
  }

  return <RowTypography>{value}</RowTypography>;
};

const PlatformFormatter = ({ row }: { row: MetadataRow }) => {
  const errorMessage = row.errors?.length ? row.errors.join('\n') : '';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <RowTypography>{row.env ? row.env : '--'}</RowTypography>
      </Box>
      {errorMessage ? (
        <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{errorMessage}</Box>} placement="top">
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help', flexShrink: 0, ml: 1 }}>
            <WarningIcon />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
};
const metadataColumns: Array<Column<MetadataRow>> = METADATA_REQUIRED_HEADERS.map((key) => ({
  key,
  name: formatFieldLabel(key).toUpperCase(),
  formatter: ({ row }) => {
    if (key === 'env') {
      return <PlatformFormatter row={row} />;
    }

    return <TextFormatter row={row} field={key} />;
  },
  width: 220,
  minWidth: 180,
}));

const normalizeMetadataRows = (rows: Array<Record<string, any>>): MetadataRow[] => {
  const normalizedRows: MetadataRow[] = rows.map((row) => normalizeMetadataRow(row));

  return normalizedRows.map((normalized, index) => {
    const errors = getRowErrors(normalized);
    return {
    ...normalized,
    id: index,
    isValid: errors.length === 0,
    errors,
    selected: false,
  };
});
}


const getMetadataRowClass = (row: MetadataRow) => (row.errors && row.errors.length > 0 ? 'bulk-upload-invalid-row' : '');

const parseMetadataExcelFile = async (file: File) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true, });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, { defval: '', raw: false, });

  if (!rawRows.length) {
    throw new Error('Uploaded file is empty or contains no valid rows.');
  }

  const rawHeadersSet = new Set<string>(Object.keys(rawRows[0]).map((header) => normalizeHeaderKey(header)));
  const normalizedRequiredHeaders = METADATA_REQUIRED_HEADERS.map((header) => normalizeHeaderKey(header));
  const missingHeaders = normalizedRequiredHeaders.filter((header) => !rawHeadersSet.has(header));

  if (missingHeaders.length) {
    throw new Error(`Missing required columns: ${missingHeaders.map(formatFieldLabel).join(', ')}`);
  }

  return rawRows.filter((row) => {
    const values = Object.values(row).map((value) => (value == null ? '' : String(value).trim()));
    return values.some((value) => value !== '');
  });
};

export const MetadataUpdates = ({ open, onCloseAction }: MetadataUpdatesProps) => {

  const handleApplyUpdates = async (rows: MetadataRow[]) => {
  const validRows = rows.map((row) => {
  const cleanedRow: Record<string, any> = {};

  Object.keys(row).forEach((key) => {
    if (
      key === 'id' ||
      key === 'isValid' ||
      key === 'errors' ||
      key === 'selected'
    ) {
      return;
    }

    const value = normalizeCellValue(row[key], key);

    cleanedRow[key] = value;
  });

  return cleanedRow;
});

  const worksheet = XLSX.utils.json_to_sheet(validRows);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Metadata');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob(
    [excelBuffer],
    {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }
  );

  const file = new File(
    [blob],
    'metadata_upload.xlsx',
    {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }
  );

  const formData = new FormData();
  formData.append('file', file);

  await uploadJobMetadata(formData);
};

  const handleParseMetadataFile = async (file: File) => {
    return parseMetadataExcelFile(file);
  };

  return (
    <BulkUploadDialog<MetadataRow>
      open={open}
      onCloseAction={onCloseAction}
      title="Bulk Upload"
      steps={['Upload File', 'Confirm & Add']}
      columns={metadataColumns}
      rowKey="id"
      sampleFileName="sample_metadata_template.xlsx"
      sampleHeaders={METADATA_REQUIRED_HEADERS}
      sampleRows={sampleRows}
      normalizeRows={normalizeMetadataRows}
      parseFile={handleParseMetadataFile}
      onApplyUpdates={handleApplyUpdates}
      rowClass={getMetadataRowClass}
      uploadTitle="Upload a File to Import"
      uploadTooltip="Upload an XLSX file with metadata"
      dropzoneMessage="Drop .xlsx file or"
      acceptedFileExtensions={['.xlsx']}
      acceptedMimeTypes={{
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      }}
      downloadButtonLabel="Download Sample Metadata Template"
      reUploadButtonLabel="Re-upload File"
      cancelButtonLabel="CANCEL"
      actionButtonLabel="UPDATE"
      showValidOnlyLabel="Show valid records only"
      successTitle={(validCount) => `Metadata has been updated for ${validCount} pipelines successfully.`}
      successDescription="Would you like to add more?"
    />
  );
};

export default MetadataUpdates;
